import { useEffect, useState } from "react";
import { fleetApi } from "@/lib/api";
import { STATUS_CONFIG, ASSET_TYPE_CONFIG } from "@/lib/constants";
import { Plus, Truck, MagnifyingGlass, Pencil, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import AssetFormDialog from "@/components/assets/AssetFormDialog";
import { LLSelect } from "@/components/ui/ll-select";

export default function AssetsPage() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const data = await fleetApi.list();
      setAssets(data);
    } catch (e) {
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (data) => {
    try {
      if (editing) {
        await fleetApi.update(editing.id, data);
        toast.success(`Asset "${data.name}" updated`);
      } else {
        await fleetApi.create(data);
        toast.success(`Asset "${data.name}" created`);
      }
      setOpenForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Failed to save asset");
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete ${asset.name}?`)) return;
    try {
      await fleetApi.remove(asset.id);
      toast.success(`Asset "${asset.name}" removed`);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const filtered = assets.filter((a) => {
    if (search && !a.name.toLowerCase().includes(search.toLowerCase()) &&
        !(a.driver || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter !== "all" && a.type !== typeFilter) return false;
    if (statusFilter !== "all" && a.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="h-[calc(100vh-72px)] flex flex-col" data-testid="assets-page">
      {/* Toolbar */}
      <div className="border-b border-[#2a2a2a] p-4 lg:p-6 flex flex-wrap items-center gap-3 bg-[#0d0d0d]">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717a]" />
            <input
              type="text"
              placeholder="Search assets, drivers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
              data-testid="search-input"
            />
          </div>
        </div>

        <LLSelect
          value={typeFilter}
          onChange={setTypeFilter}
          testId="type-filter"
          className="min-w-[150px]"
          options={[
            { value: "all", label: "All Types" },
            ...Object.entries(ASSET_TYPE_CONFIG).map(([k, v]) => ({ value: k, label: v.label })),
          ]}
        />

        <LLSelect
          value={statusFilter}
          onChange={setStatusFilter}
          testId="status-filter"
          className="min-w-[150px]"
          options={[
            { value: "all", label: "All Status" },
            ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label })),
          ]}
        />

        <button
          onClick={() => { setEditing(null); setOpenForm(true); }}
          className="ml-auto flex items-center gap-2 px-4 py-2.5 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-xs uppercase tracking-[0.2em] font-bold transition-colors"
          data-testid="new-asset-btn"
        >
          <Plus size={16} weight="bold" />
          New Asset
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        {loading ? (
          <div className="text-[#71717a] text-sm uppercase tracking-wider">Loading fleet inventory…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[#2a2a2a] p-12 text-center">
            <Truck size={36} className="mx-auto text-[#71717a] mb-3" />
            <div className="text-sm text-[#a1a1aa]">No assets match your filters</div>
          </div>
        ) : (
          <div className="border border-[#2a2a2a] bg-[#0d0d0d] overflow-hidden">
            <table className="w-full" data-testid="assets-table">
              <thead>
                <tr className="bg-[#1a1a1a] border-b border-[#2a2a2a]">
                  {["Asset", "Type", "Status", "Driver", "Load", "Fuel", "Location", "Actions"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#71717a] font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const status = STATUS_CONFIG[a.status] || STATUS_CONFIG.idle;
                  const type = ASSET_TYPE_CONFIG[a.type] || { label: a.type };
                  const loadPct = a.capacity_lbs > 0
                    ? Math.round((a.current_load_lbs / a.capacity_lbs) * 100)
                    : 0;
                  const overweight = a.current_load_lbs > a.capacity_lbs && a.capacity_lbs > 0;
                  // Service status — overdue/due_soon when interval is set
                  let serviceState = null;
                  if ((a.service_interval_hours || 0) > 0) {
                    const hoursTo = (a.last_serviced_hours || 0) + a.service_interval_hours - (a.hours_meter || 0);
                    if (hoursTo <= 0) serviceState = { label: "Overdue", color: "#ef4444", hoursTo };
                    else if (hoursTo <= Math.max(50, Math.floor(a.service_interval_hours / 10))) serviceState = { label: `Due ${hoursTo}h`, color: "#ff8c00", hoursTo };
                  }
                  return (
                    <tr key={a.id} className="border-b border-[#2a2a2a] hover:bg-[#1a1a1a] transition-colors">
                      <td className="px-4 py-4">
                        <div className="font-mono font-bold text-sm flex items-center gap-2">
                          {a.name}
                          {serviceState && (
                            <span
                              className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold border"
                              style={{ color: serviceState.color, borderColor: serviceState.color, background: serviceState.color + "15" }}
                              title={`Service ${serviceState.label.toLowerCase()} (interval ${a.service_interval_hours}h)`}
                              data-testid={`service-badge-${a.name}`}
                            >
                              {serviceState.label}
                            </span>
                          )}
                        </div>
                        {a.license_plate && (
                          <div className="text-[10px] text-[#71717a] uppercase tracking-wider font-mono">
                            {a.license_plate}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-xs uppercase tracking-wider text-[#a1a1aa]">{type.label}</td>
                      <td className="px-4 py-4">
                        <span
                          className="inline-flex items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
                          style={{ background: status.bg, color: status.color, border: `1px solid ${status.color}` }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.color }}></span>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-xs text-[#a1a1aa]">{a.driver || "—"}</td>
                      <td className="px-4 py-4">
                        {a.capacity_lbs > 0 ? (
                          <div>
                            <div className="font-mono text-xs" style={{ color: overweight ? "#ef4444" : "#fff" }}>
                              {a.current_load_lbs.toLocaleString()} / {a.capacity_lbs.toLocaleString()}
                            </div>
                            <div className="h-1 bg-[#1a1a1a] mt-1 w-24">
                              <div
                                className="h-full"
                                style={{
                                  width: `${Math.min(100, loadPct)}%`,
                                  background: overweight ? "#ef4444" : loadPct > 90 ? "#ff8c00" : "#4682b4",
                                }}
                              ></div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[#71717a] text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {a.fuel_level > 0 ? (
                          <span className="font-mono text-xs" style={{
                            color: a.fuel_level < 25 ? "#ef4444" : a.fuel_level < 50 ? "#ff8c00" : "#22c55e"
                          }}>
                            {a.fuel_level}%
                          </span>
                        ) : (
                          <span className="text-[#71717a] text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 font-mono text-xs text-[#a1a1aa]">
                        {a.lat?.toFixed(3)}, {a.lng?.toFixed(3)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setEditing(a); setOpenForm(true); }}
                            className="w-8 h-8 hover:bg-[#333] flex items-center justify-center text-[#a1a1aa] hover:text-white transition-colors"
                            data-testid={`edit-asset-${a.name}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(a)}
                            className="w-8 h-8 hover:bg-[#ef4444]/20 flex items-center justify-center text-[#a1a1aa] hover:text-[#ef4444] transition-colors"
                            data-testid={`delete-asset-${a.name}`}
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openForm && (
        <AssetFormDialog
          asset={editing}
          onClose={() => { setOpenForm(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
